import {useEffect, useState} from "react";
import moment from "moment";
import {useDispatch, useSelector} from "react-redux";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import VLFModal from "@/components/Modal";
import VLFTextField from "@/components/Input/TextField";
import ViewField from "@/components/Input/ViewField";
import StatusBadge from "@/components/StatusBadge";
import ChoiceChips from "@/components/ChoiceChips";

import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

import KeyIcon from "@mui/icons-material/Key";
import LogoutIcon from "@mui/icons-material/Logout";

import {UserAPI} from "@/api/user";
import {logout} from "@/actions";
import {AREAS, accessLevel, isPlayer} from "@/utils/access";
import {
	getThemePreference,
	isGlassOn,
	setGlass,
	setThemePreference,
} from "@/style/utils";
import {
	ACCESS_LEVELS,
	PERMISSION_AREAS,
	REQUIRED_FIELD,
	SUCCESSFUL_OPERATION,
} from "@/utils/constants";

import "./index.scss";

const THEME_OPTIONS = [
	{value: "light", label: "Chiaro"},
	{value: "dark", label: "Scuro"},
	{value: "system", label: "Come il dispositivo"},
];

// Aree mostrate all'utente: quelle che il frontend usa davvero
const AREA_LIST = [
	{key: AREAS.DIREZIONE, label: "Direzione"},
	...PERMISSION_AREAS.filter((a) => Object.values(AREAS).includes(a.key)),
];

const EMPTY_PASSWORD = {oldPassword: "", newPassword: "", confirm: ""};

// Il mio account: dati, accessi e cambio password dell'utente collegato
function AccountPage(props) {
	const {handleNotification} = props;
	const dispatch = useDispatch();
	const user = useSelector((state) => state?.user);

	const [loading, setLoading] = useState(false);
	const [passwords, setPasswords] = useState(EMPTY_PASSWORD);
	const [errors, setErrors] = useState({});
	const [openLogout, setOpenLogout] = useState(false);
	const [glass, setGlassState] = useState(isGlassOn);
	// Scheda utente dal server (email, telefono, ultimo cambio password):
	// se il ruolo non può leggerla restano i dati dell'accesso
	const [details, setDetails] = useState(null);

	useEffect(() => {
		if (!user?.userName) return;
		UserAPI.getById(user.userName)
			.then((res) => setDetails(res || null))
			.catch(() => setDetails(null));
	}, [user?.userName]);

	const setField = (key, value) => {
		setPasswords({...passwords, [key]: value});
		setErrors({});
	};

	const changePassword = async () => {
		const next = {};
		["oldPassword", "newPassword", "confirm"].forEach((k) => {
			if (!passwords[k]) next[k] = REQUIRED_FIELD;
		});
		if (!next.newPassword && passwords.newPassword === passwords.oldPassword)
			next.newPassword = "Deve essere diversa da quella attuale";
		if (!next.confirm && passwords.confirm !== passwords.newPassword)
			next.confirm = "Le due password non coincidono";
		setErrors(next);
		if (Object.keys(next).length) return;

		setLoading(true);
		try {
			await UserAPI.changePassword({
				userName: user?.userName,
				oldPassword: passwords.oldPassword,
				newPassword: passwords.newPassword,
			});
			setPasswords(EMPTY_PASSWORD);
			setDetails((d) => d && {...d, lastPasswordChangedDate: moment()});
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch {
			handleNotification(
				"Password non cambiata: controlla quella attuale e che la nuova rispetti le regole (lunghezza, maiuscole, numeri, simboli).",
				"error",
			);
		} finally {
			setLoading(false);
		}
	};

	const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ");

	const passwordField = (key, label) => (
		<VLFTextField
			type="password"
			label={label}
			required
			value={passwords[key]}
			onChange={(e) => setField(key, e.target.value)}
			error={!!errors[key]}
			helperText={errors[key]}
		/>
	);

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				current="Il mio account"
				actions={
					<MainActions
						actions={[
							{
								icon: <LogoutIcon />,
								title: "Esci",
								variant: "outlined",
								onClick: () => setOpenLogout(true),
							},
						]}
					/>
				}
			/>
			<div className="account">
				<VLFCard
					title="I tuoi dati"
					content={
						<ViewField
							configuration={[
								{
									label: "Nome",
									value: fullName || "—",
									size: "col-12 col-md-6",
								},
								{
									label: "Utente",
									value: user?.userName,
									size: "col-12 col-md-6",
								},
								{
									label: "Email",
									value: details?.email || user?.email || "—",
									size: "col-12 col-md-6",
								},
								{
									label: "Ruolo",
									value: user?.roleName,
									size: "col-12 col-md-6",
								},
								{
									label: "Telefono",
									value: details?.telephone || "—",
									size: "col-12 col-md-6",
								},
								{
									label: "Ultimo cambio password",
									value:
										details?.lastPasswordChangedDate &&
										moment(details.lastPasswordChangedDate).year() > 2000
											? moment(details.lastPasswordChangedDate).format(
													"DD/MM/YYYY",
												)
											: "—",
									size: "col-12 col-md-6",
								},
							]}
						/>
					}
				/>

				<VLFCard
					title="Cosa puoi vedere e modificare"
					content={
						isPlayer(user) ? (
							<div className="account__note">
								Vedi i tuoi dati: profilo, allenamenti, partite e misure del
								benessere.
							</div>
						) : (
							<div className="account__areas">
								{AREA_LIST.map((a) => {
									const level = accessLevel(user, a.key);
									return (
										<div key={a.key} className="account__area">
											<span>{a.label}</span>
											<StatusBadge
												tone={
													level === ACCESS_LEVELS.FULL_CONTROL
														? "good"
														: level
															? "neutral"
															: "warning"
												}
												label={
													level === ACCESS_LEVELS.FULL_CONTROL
														? "Vedi e modifichi"
														: level
															? "Solo lettura"
															: "Nessun accesso"
												}
											/>
										</div>
									);
								})}
							</div>
						)
					}
				/>

				<VLFCard
					title="Aspetto"
					content={
						<>
							<div className="account__label">Tema</div>
							<ChoiceChips
								options={THEME_OPTIONS}
								value={getThemePreference()}
								onChange={(value) =>
									value !== getThemePreference() && setThemePreference(value)
								}
							/>
							<FormControlLabel
								control={
									<Checkbox
										size="small"
										checked={glass}
										onChange={(e) => {
											setGlassState(e.target.checked);
											setGlass(e.target.checked);
										}}
									/>
								}
								label="Effetto vetro su intestazioni, schede e finestre"
							/>
							<div className="account__note">
								Tema e vetro valgono per questo dispositivo. Se lo schermo è
								lento o preferisci superfici piene, togli il vetro.
							</div>
						</>
					}
				/>

				<VLFCard
					title="Cambia password"
					content={
						<>
							<div className="row">
								<div className="col-12 col-md-4">
									{passwordField("oldPassword", "Password attuale")}
								</div>
								<div className="col-12 col-md-4">
									{passwordField("newPassword", "Nuova password")}
								</div>
								<div className="col-12 col-md-4">
									{passwordField("confirm", "Ripeti la nuova password")}
								</div>
							</div>
							<div className="account__password-footer">
								<div className="account__note">
									Hai dimenticato la password? Chiedi a chi gestisce gli utenti
									del club di reimpostarla.
								</div>
								<MainActions
									actions={[
										{
											icon: <KeyIcon />,
											label: "Cambia password",
											onClick: changePassword,
										},
									]}
								/>
							</div>
						</>
					}
				/>
			</div>
			<VLFModal
				maxWidth="xs"
				open={openLogout}
				title="Esci"
				content={<div className="mt-2">Vuoi uscire dall'app?</div>}
				confirm={() => dispatch(logout())}
				close={() => setOpenLogout(false)}
				confirmLabel="Esci"
			/>
		</Page>
	);
}

export default AccountPage;
